/**
 * Settings Layout Component
 * 
 * Two-column layout with left sidebar navigation and right content area
 * Following the design specifications for Settings expansion
 * 
 * Features:
 * - Responsive sidebar navigation
 * - Nested routing support
 * - Mobile-responsive (sidebar collapses to top navigation)
 * - WCAG 2.2 AA compliant
 * 
 * @see specs/003-categorisation-feature/spec.md (Part 1: Settings Page Expansion)
 */

import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SettingsNav } from './SettingsNav';
import { NavigationGuardProvider } from '../../contexts/NavigationGuardContext';
import { useNavigationGuardState } from './useNavigationGuardState';
import { PageHeader } from '../../components/PageHeader';

interface SettingsLayoutProps {
  children?: React.ReactNode;
}

export function SettingsLayout({ children }: SettingsLayoutProps): React.ReactElement {
   
  void useLocation();
  const { hasUnsavedChanges, onNavigationAttempt } = useNavigationGuardState();

  return (
    <NavigationGuardProvider
      hasUnsavedChanges={hasUnsavedChanges}
      onNavigationAttempt={onNavigationAttempt}
    >
      <div className="space-y-6">
        {/* Settings Header */}
        <PageHeader
          title="Settings"
          description="Manage your account preferences and application settings"
          headingLevel="h1"
        />

        {/* Two-column layout */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Sidebar Navigation */}
          <aside className="w-full lg:w-64 flex-shrink-0">
            <SettingsNav />
          </aside>

          {/* Right Content Area */}
          <main className="flex-1 min-w-0" id="main-content">
            {children || <Outlet />}
          </main>
        </div>
      </div>
    </NavigationGuardProvider>
  );
}

